import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Modal, Button, Textarea } from 'flowbite-react'
import DOMPurify from 'dompurify'
import katex from 'katex'
import 'katex/dist/katex.min.css'

type Props = {
  show: boolean
  onClose: () => void
  initialValue?: string
  onInsert: (tex: string) => void
}

const SYMBOL_CATEGORIES: { [k: string]: { tex: string; title?: string }[] } = {
  'Greek': [
    { tex: '\\alpha' }, { tex: '\\beta' }, { tex: '\\gamma' }, { tex: '\\delta' },
    { tex: '\\epsilon' }, { tex: '\\varepsilon' }, { tex: '\\zeta' }, { tex: '\\eta' },
    { tex: '\\theta' }, { tex: '\\iota' }, { tex: '\\kappa' }, { tex: '\\lambda' },
    { tex: '\\mu' }, { tex: '\\nu' }, { tex: '\\xi' }, { tex: '\\pi' },
    { tex: '\\rho' }, { tex: '\\sigma' }, { tex: '\\tau' }, { tex: '\\upsilon' },
    { tex: '\\phi' }, { tex: '\\chi' }, { tex: '\\psi' }, { tex: '\\omega' },
    { tex: '\\Gamma' }, { tex: '\\Delta' }, { tex: '\\Theta' }, { tex: '\\Lambda' },
    { tex: '\\Xi' }, { tex: '\\Pi' }, { tex: '\\Sigma' }, { tex: '\\Upsilon' },
    { tex: '\\Phi' }, { tex: '\\Psi' }, { tex: '\\Omega' },
  ],
  'Misc': [
    { tex: '\\times' }, { tex: '\\div' }, { tex: '\\cdot' }, { tex: '\\pm' },
    { tex: '\\mp' }, { tex: '\\ast' }, { tex: '\\circ' }, { tex: '\\bullet' },
    { tex: '\\oplus' }, { tex: '\\ominus' }, { tex: '\\otimes' }, { tex: '\\oslash' },
    { tex: '\\odot' }, { tex: '\\dagger' }, { tex: '\\ddagger' }, { tex: '\\vee' },
    { tex: '\\wedge' }, { tex: '\\cap' }, { tex: '\\cup' }, { tex: '\\emptyset' },
    { tex: '\\infty' }, { tex: '\\partial' }, { tex: '\\forall' }, { tex: '\\exists' },
  ],
  'Relations': [
    { tex: '\\leq' }, { tex: '\\geq' }, { tex: '\\ll' }, { tex: '\\gg' },
    { tex: '\\approx' }, { tex: '\\equiv' }, { tex: '\\neq' }, { tex: '\\sim' },
    { tex: '\\subset' }, { tex: '\\supset' }, { tex: '\\subseteq' }, { tex: '\\supseteq' },
    { tex: '\\in' }, { tex: '\\notin' },
  ],
  'Math Ops': [
    // basic: fraction, roots, superscripts/subscripts
    { tex: '\\frac{a}{b}' }, { tex: '\\sqrt{x}' }, { tex: '\\sqrt[n]{x}' }, { tex: 'x^{b}' },
    { tex: 'x_{a}' }, { tex: 'x^{b}' },

    // accents and bar/hats
    { tex: '\\overline{x}' }, { tex: '\\hat{x}' }, { tex: '\\tilde{x}' }, { tex: '\\dot{x}' },

    // big operators
    { tex: '\\bigcap_{a}^{b}' }, { tex: '\\bigcup_{a}^{b}' }, { tex: '\\bigsqcup_{a}^{b}' }, { tex: '\\prod_{a}^{b}' },
    { tex: '\\coprod_{a}^{b}' }, { tex: '\\bigvee_{a}^{b}' },

    // delimiters and norms
    { tex: '\\left(\\right)' }, { tex: '\\left[\\right]' }, { tex: '\\left\\{\\right\\}' }, { tex: '\\lVert x\\rVert' },
    { tex: '\\lvert x\\rvert' }, { tex: '\\|x\\|' },

    // integrals
    { tex: '\\int_a^b' }, { tex: '\\iint' }, { tex: '\\iiint' }, { tex: '\\oint' },
    { tex: '\\int_a^b f(x) \\; dx' }, { tex: '\\int\\limits_a^b' },

    // sums & limits
    { tex: '\\sum_{a}^{b}' }, { tex: '\\lim_{a\\to b}' }, { tex: '\\limsup' }, { tex: '\\liminf' },
  ],
  'Arrows': [
    { tex: '\\leftarrow' }, { tex: '\\rightarrow' }, { tex: '\\leftrightarrow' }, { tex: '\\Rightarrow' },
    { tex: '\\Leftarrow' }, { tex: '\\uparrow' }, { tex: '\\downarrow' }, { tex: '\\updownarrow' },
    { tex: '\\Longleftarrow' }, { tex: '\\Longrightarrow' }, { tex: '\\longleftrightarrow' },
  ],
}

export default function LaTeXBuilder({ show, onClose, initialValue = '', onInsert }: Props) {
  const [tex, setTex] = useState(initialValue)
  const [error, setError] = useState<string | null>(null)
  const [openCategory, setOpenCategory] = useState<string | null>(null)
  const anchorRef = React.useRef<HTMLElement | null>(null)
  const dropdownRef = React.useRef<HTMLDivElement | null>(null)
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties | null>(null)

  useEffect(() => {
    setTex(initialValue)
  }, [initialValue])

  useEffect(() => {
    try {
      // try rendering to detect errors
      katex.renderToString(tex || '', { throwOnError: true })
      setError(null)
    } catch (e: any) {
      setError(e?.message || 'KaTeX render error')
    }
  }, [tex])

  // compute dropdown position whenever openCategory changes
  useEffect(() => {
    if (!openCategory || !anchorRef.current) {
      setDropdownStyle(null)
      return
    }

    const rect = anchorRef.current.getBoundingClientRect()
    // decide whether to place below or above depending on available space
    const availableBelow = window.innerHeight - rect.bottom
    const estimatedHeight = 360
    const placeAbove = availableBelow < estimatedHeight
    const top = placeAbove
      ? Math.max(8, rect.top + window.scrollY - estimatedHeight - 6)
      : rect.bottom + window.scrollY + 6

    const style: React.CSSProperties = {
      position: 'absolute',
      left: rect.left + window.scrollX,
      top,
      zIndex: 30000000,
    }
    setDropdownStyle(style)
  }, [openCategory])

  // close dropdown when clicking outside
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const target = e.target as Node | null
      if (!openCategory) return
      if (dropdownRef.current && dropdownRef.current.contains(target)) return
      if (anchorRef.current && anchorRef.current.contains(target)) return
      setOpenCategory(null)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [openCategory])

  const handleInsert = () => {
    // Return raw tex wrapped in $ delimiters (caller may choose differently)
    onInsert(tex)
    onClose()
  }

  if (!show) return null

  const modal = (
    <div className="fixed inset-0 z-[20000000] flex items-center justify-center p-4">
      <div className="w-full max-w-3xl max-h-[85vh] overflow-auto">
        <div className="bg-white dark:bg-[#071025] rounded-2xl shadow-2xl overflow-hidden border">
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <div>
              <h3 className="text-xl font-semibold">Insert Math</h3>
              <div className="text-sm text-gray-500 dark:text-gray-400">Type or click symbols to build LaTeX</div>
            </div>
            <button aria-label="Close" onClick={onClose} className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-700 dark:text-gray-200" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">LaTeX</label>
                <Textarea value={tex} onChange={(e) => setTex((e.target as HTMLTextAreaElement).value)} rows={8} className="min-h-[160px]" />

                <div className="mt-4">
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(SYMBOL_CATEGORIES).map(([cat, _items]) => (
                      <div key={cat} className="relative">
                        <Button
                          size="xs"
                          onClick={(e) => {
                            anchorRef.current = e.currentTarget as HTMLElement
                            setOpenCategory((prev) => (prev === cat ? null : cat))
                          }}
                          aria-expanded={openCategory === cat}
                          aria-haspopup="true"
                        >
                          {cat}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div>
                <div className="text-sm text-gray-500 mb-2">Preview</div>
                <div className="p-4 border rounded-lg bg-white dark:bg-[#071025] min-h-[160px]">
                  {tex ? (
                    <div className="prose-sm" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(katex.renderToString(tex, { throwOnError: false })) }} />
                  ) : (
                    <div className="text-xs text-gray-400">No input yet</div>
                  )}
                  {error ? <div className="text-xs text-red-600 mt-2">{error}</div> : null}
                </div>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t flex justify-end gap-3">
            <Button onClick={handleInsert} className="bg-blue-600 hover:bg-blue-700">Insert</Button>
            <Button color="gray" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </div>
    </div>
  )

  if (typeof document === 'undefined') return null
  // also render the open category dropdown into the body so it won't be clipped by the modal
  const dropdownPortal = openCategory && dropdownStyle ? createPortal(
    <div ref={dropdownRef} style={dropdownStyle}>
      <div className="bg-white dark:bg-[#071025] border rounded shadow p-3 min-w-[520px]">
        <div className="grid grid-cols-4 gap-4 max-h-72 overflow-auto p-1">
          {SYMBOL_CATEGORIES[openCategory].map((it, idx) => (
            <button
              key={idx}
              type="button"
              title={it.title || it.tex}
              className="text-lg leading-tight p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
              onClick={() => setTex((prev) => prev + (prev ? ' ' : '') + it.tex)}
            >
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(katex.renderToString(it.tex, { throwOnError: false })) }} />
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  ) : null

  return (
    <>
      {createPortal(
        <>
          {/* backdrop */}
          <div className="fixed inset-0 bg-black/50 z-[19000000]" onClick={onClose} />
          {modal}
        </>,
        document.body,
      )}
      {dropdownPortal}
    </>
  )
}
